import { type EventId } from "../../world/types";

/*
 * The pieces every panel is built out of (chunk U-0's split of `panels.ts`).
 *
 * A helper lands here the moment a SECOND surface wants it, and never before —
 * a shared module that fills up with one-use functions is a second place to
 * look for the code that draws one panel. The five here are used by between
 * three and eight of them.
 *
 * `button` is the sharpest example and the reason this file is not optional:
 * every panel that offers a choice draws the same button, and a button that
 * forgot to stop the click reaching the canvas would put the player back into
 * the game (`hud/panels.ts`'s note on the two input rules).
 */

/** Renders an event id the way every panel here renders one. */
export function eventTag(event: EventId): string {
  return `#${event}`;
}

/** A section heading inside a panel body. */
export function heading(text: string): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__panel-heading";
  element.textContent = text;

  return element;
}

/**
 * A button inside a panel.
 *
 * It turns pointer events back on for itself and stops the click reaching the
 * canvas, which are the two rules {@link SaveButton} states: down there a click
 * is the gesture that asks for the pointer lock, and a player who pressed a
 * button in a panel did not ask to be put back into the game.
 */
export function button(label: string, onPress: () => void, disabled = false): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = "hud__panel-button";
  element.textContent = label;
  element.disabled = disabled;
  element.addEventListener("click", (event) => {
    event.stopPropagation();
    onPress();
  });

  return element;
}

/** "one thing", "three things" — the plural the panels keep saying. */
export function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/** A single line of body text, for the empty states. */
export function line(text: string): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__panel-empty";
  element.textContent = text;

  return element;
}

/*
 * ------------------------- The reading window (chunk U-2) -------------------------
 *
 * The frame every in-world reading opens in, by the owner's call of 2026-09-01:
 * a book, the market board, a chronicle — and, when there is one, a grave or a
 * shelf. Three surfaces build the same chassis, so it lands here under this
 * file's own rule and not before.
 *
 * **It is one component and not three boxes**, which is the whole point of the
 * ruling: the eras is the surface the owner pointed at, and the other two moved
 * into its frame rather than each keeping a corner of the screen. What a surface
 * still owns is its *body* — pinned slips, plain record lines, a title page —
 * and the words in its head.
 */

/** Which `hud__*` names a reading's head hangs on its three lines. */
export interface ReadingShape {
  /**
   * The surface's own modifier: the whole of `hud__panel--market`,
   * `hud__panel--book`, `hud__panel--eras` after the two underscores.
   */
  readonly surface: string;

  /**
   * The lab's narrower measure, for a page of *rows* rather than of prose.
   *
   * The board wears it and the two books do not: a Saga line is a whole sentence
   * with a year in it and eight of them want the wide measure, where a column of
   * commodities at that width is a table with a lot of air in it.
   */
  readonly narrow?: boolean;

  /**
   * The class on the gold micro-label above the title, or absent for a head
   * without one.
   */
  readonly eyebrow?: string;

  /** The class on the large line. Always drawn. */
  readonly title: string;

  /**
   * The class on the italic line under it, or absent for a head without one.
   *
   * Hidden while it is empty rather than drawn blank, because a head whose
   * second line is a gap reads as a head missing a line.
   */
  readonly under?: string;
}

/** What {@link readingWindow} builds, in the order a surface fills it. */
export interface ReadingWindow {
  /**
   * The centred layer, and the element the surface's `hud__panel--*` name is on:
   * what {@link ReadingWindow.hidden} toggles, and what the veil is painted on.
   *
   * It covers the whole frame and takes no pointer events; the page below does
   * (`ui/base.css`'s note on `.hud`). That is what leaves the eras' backdrop —
   * which is written before this and is the way out of the opening — still able
   * to hear the click that begins a session.
   */
  readonly element: HTMLElement;

  /** The frame itself. The one thing on the screen a click may land on. */
  readonly page: HTMLElement;

  /** The gold micro-label, or `null` where the shape asked for none. */
  readonly eyebrow: HTMLElement | null;

  /** The large line. */
  readonly title: HTMLElement;

  /** The italic line under it, or `null` where the shape asked for none. */
  readonly under: HTMLElement | null;

  /** The measured body. What each surface fills in its own register. */
  readonly body: HTMLElement;

  /**
   * Which key shuts the reading, drawn **outside** the frame.
   *
   * The keys belong to the player and the page belongs to the world, so nothing
   * about a keyboard is printed inside the book (the lab's rule, and the one
   * piece of this composition that is a statement rather than a layout). It is
   * still inside {@link ReadingWindow.element}, which is what keeps every
   * `.hud__panel--x .hud__panel-hint` a check writes resolving.
   */
  readonly hint: HTMLElement;
}

/**
 * Builds the centred page every in-world reading is read on.
 *
 * The kit's `.reading-*` composition (`ui/kit.css`) under the client's own
 * `hud__*` names, which is decision D2 exactly: the names are the identity and
 * the kit composes alongside them. Nothing here is filled in — a surface sets
 * its own words and appends whatever it puts between the body and the hint (the
 * eras' foot is the only one).
 *
 * The caller appends {@link ReadingWindow.element} to the HUD root itself,
 * because the order it is appended in **is** the stacking order
 * (`hud/hud.ts`) and one of the three has a backdrop that has to go first.
 */
export function readingWindow(shape: ReadingShape): ReadingWindow {
  const element = document.createElement("div");
  // **Not a `hud__panel`**, and that is the one structural claim this component
  // makes: the chassis in `ui/panel.css` is a box in a corner with a plate and a
  // padding, and a full-frame layer would have had to cancel six of its
  // declarations to become one. The surface's own `hud__panel--*` name is the
  // identity and stays (D2); what it composes with is the kit's centre, and
  // `ui/reading.css` states the `[hidden]` rule the chassis used to lend it.
  element.className = `hud__panel--${shape.surface} hud__reading reading-centre`;
  element.hidden = true;

  const page = document.createElement("article");
  page.className = `hud__reading-page game-panel reading-page${
    shape.narrow === true ? " reading-page--narrow" : ""
  }`;

  const head = document.createElement("header");
  head.className = "hud__reading-head reading-head";

  // The kit's `.eyebrow` is deliberately NOT put on it, and that is invariant 4
  // rather than a taste call: the kit uppercases that class, and the one head
  // here that has an eyebrow is the book of eras, whose label carries a
  // settlement's name — *"the book of eras — kept in Jorgarth"*. `reading.css`
  // gives it the same gold micro-label without the transform.
  const eyebrow = shape.eyebrow === undefined ? null : document.createElement("div");

  if (eyebrow !== null) {
    eyebrow.className = `${shape.eyebrow ?? ""} hud__reading-eyebrow`;
    head.append(eyebrow);
  }

  // `<h1>` because it is one: the reading window is the only surface in this
  // client that is a page of a document rather than a readout, and the kit's
  // type is written against the element.
  const title = document.createElement("h1");
  title.className = `${shape.title} reading-title`;
  head.append(title);

  const under = shape.under === undefined ? null : document.createElement("p");

  if (under !== null) {
    under.className = `${shape.under ?? ""} reading-under`;
    under.hidden = true;
    head.append(under);
  }

  // The title-page ornament, and the page's one piece of gold. Decorative and
  // said so, because a screen reader has no use for a diamond between two rules.
  const ornament = document.createElement("div");
  ornament.className = "rule-diamond";
  ornament.setAttribute("aria-hidden", "true");

  const diamond = document.createElement("span");
  diamond.className = "objective-diamond";
  ornament.append(diamond);
  head.append(ornament);

  const body = document.createElement("div");
  body.className = "hud__panel-body reading-body";

  const hint = document.createElement("div");
  hint.className = "hud__panel-hint reading-hint";

  page.append(head, body);
  element.append(page, hint);

  return { element, page, eyebrow, title, under, body, hint };
}

/**
 * Sets a head's under-line, and hides it when there is nothing to say.
 *
 * One place rather than three, because the *hiding* is the part a surface gets
 * wrong: an empty italic line under a title is a gap that reads as a missing
 * sentence, and the eras has been drawing its second naming surface this way
 * since Phase 6 (`hud/panels.ts`'s `ErasPanel`).
 */
export function saidUnder(under: HTMLElement | null, text: string): void {
  if (under === null) {
    return;
  }

  under.textContent = text;
  under.hidden = text === "";
}

/*
 * --------------------------- The menu's pages (chunk U-3) ---------------------------
 *
 * The journal, the map and what is in the player's hands stopped being three
 * overlays in three corners and became four pages of one screen (the owner's
 * direction of 2026-09-01, `ui-lab/surfaces/menu-tabs.html`). Three surfaces
 * that used to attach themselves to the HUD root now fill a page of it instead,
 * so the thing they are handed is this rather than a `root`.
 *
 * **It is an interface and not the class**, and that is the point of putting it
 * here: `map/panel.ts` lives outside `hud/` and would otherwise have to import
 * the menu to be a page of it. What a surface needs to know about the screen is
 * four verbs wide, and none of them is the screen.
 */

/** Which page of the menu a surface fills. */
export type MenuPage = "you" | "journal" | "map" | "inventory";

/**
 * One page of the menu, as the surface that fills it sees it.
 *
 * The three absorbed surfaces keep the shape they always had —
 * `open` / `show(...)` / `hide()`, which is what `witness/witness.ts` calls and
 * that file is fenced out of this wave — and every one of those three answers
 * is now a question about the screen. So `open` is {@link MenuTab.showing},
 * `show` ends in {@link MenuTab.reveal} and `hide` is {@link MenuTab.close}.
 */
export interface MenuTab {
  /** Which page this is. */
  readonly page: MenuPage;

  /**
   * Where the surface builds its body.
   *
   * The element also carries the surface's own `hud__panel--*` name, so every
   * `.hud__panel--journal` and `.hud__panel--map` a check or a driver selects
   * still resolves, and its `hidden` still means *is this on screen* (plan
   * decision D2, invariant 9).
   */
  readonly body: HTMLElement;

  /** Whether the screen is open **and** showing this page. */
  readonly showing: boolean;

  /** Opens the screen on this page, after the surface has filled it. */
  reveal(): void;

  /**
   * Closes the whole screen — from this page only.
   *
   * A page that is not showing has nothing to close: the screen belongs to
   * whichever surface is on it, and `hide()` on one of the other three would
   * otherwise shut a page the player is reading.
   */
  close(): void;

  /**
   * What this page adds to the screen's foot: the keys that are its own.
   *
   * The close clause is the *screen's* and is said once — that is the whole of
   * what one screen buys over three overlays — so what a page states here is
   * only what it alone can be done to: *drag to move the paper*, *Q puts down
   * what is at the top*. Set once at construction; the foot draws whichever
   * page is showing.
   */
  note(text: string): void;
}

/*
 * --------------------------- The hotbar (chunk U-5) ---------------------------
 *
 * Ten slots over the digit keys, drawn twice: on the HUD's bottom right, and on
 * the foot of the menu's inventory page (`ui-lab/surfaces/hud.html:89`,
 * `menu-tabs.html:625`). Two surfaces want the same object, which is this
 * file's own bar for being in it.
 *
 * **The arrangement is the client's and the wield is the world's**, and that
 * split is the whole design (plan D8, `docs/decisions/player.md` §24.1): which
 * handle sits over which key is the journal's kind of state — a session's
 * arrangement, not the simulation's, and not persisted, because nothing in this
 * client persists anything. What a press *does* is one `Command::Wield`, which
 * is the simulation's own door, and the slot lights because
 * `WitnessSource.wielded` says the thing is in hand — never because this file
 * remembers pressing the key.
 *
 * **It queues rather than fires.** A slot has no world to speak to — a panel
 * never holds one — so a press lands in a queue that `main.ts` drains beside
 * the key toggles, which is `docs/decisions/input.md` §5's rule read one layer
 * up: every button is a key, and there is no second path in.
 */

/** How many slots the strip has, and therefore how many digit keys it takes. */
export const HOTBAR_SLOTS = 10;

/**
 * One thing in the hands: the simulation's word for it, and the handle a door
 * takes.
 *
 * The two halves are decision 11's split, which every read across this boundary
 * makes: the handle is what a client groups, keys and counts by, and the label
 * is prose the simulation composed out of three tables this side keeps no copy
 * of ({@link WitnessSource.itemLabel}).
 *
 * **`item` is `null` for a row this client has only the words for**, and that is
 * a transitional state with a date on it: `InventoryPanel.show` — the flat call
 * `witness/witness.ts` still makes — carries labels and no handles, so a row
 * that arrived through it is drawn and offered nothing. It disappears with that
 * method (see {@link InventoryPanel.hold}).
 */
export interface HeldThing {
  /** The display number, or `null` where only the words crossed. */
  readonly item: number | null;

  /** What it is called, in the simulation's own words. Never re-split. */
  readonly label: string;
}

/**
 * What the simulation says is in the player's hands and on their body.
 *
 * The shape chunk U-3 filed when it found the count on the inventory page
 * counting a helm against a cap that only bounds a pair of hands
 * (`docs/decisions/ui.md` §4.5): what arrived was one flat list punctuated by
 * blank rows, and a page had to read somebody else's punctuation to know how
 * full the hands were. This says it once.
 */
export interface Hands {
  /**
   * What is carried, in the simulation's carry order — **the wielded thing
   * first** (`docs/decisions/player.md` §24.2: `Wielded` is the head of the
   * list, which is what lets the weapon pick, the bow, the arrow and the drop
   * all follow the choice without one of them being edited).
   */
  readonly carried: readonly HeldThing[];

  /** What is on the body, in handle order. The first is what the doff verb takes. */
  readonly worn: readonly HeldThing[];

  /**
   * What the player has chosen to hold in front, or `null` for hands nobody has
   * arranged ({@link WitnessSource.wielded}).
   *
   * **It is not *the weapon you would swing with***, and no surface may draw it
   * as one: `combat::weapon_of` is private and wielding a boulder is legal
   * (`player.md` §24.8). What it lights is the thing in the hand.
   */
  readonly wielded: number | null;

  /**
   * The piece a nameless *put on* would put on next, or `null` for a press that
   * would do nothing ({@link WitnessSource.wearable}).
   *
   * The simulation's own pick, asked rather than guessed — which is what lets
   * this page offer the wear door on exactly one row without keeping a copy of
   * the armour table (see {@link InventoryPanel}).
   */
  readonly wearable: number | null;

  /** The arrow line, or `""` for a pair of hands with no bow and no arrows. */
  readonly quiver: string;

  /** Who is walking with the player, by name (`witness/company.ts`). */
  readonly company: readonly string[];

  /** The simulation's own cap on a pair of hands, only ever shown. */
  readonly capacity: number;

  /**
   * The flat call's remainder, and **only** the flat call's: present means *this
   * came through `InventoryPanel.show`*.
   *
   * What that method is handed is one list punctuated by blank rows, so the worn
   * things, the quiver and the company arrive as words with no section around
   * them. They are drawn in the order they were composed, and the register draws
   * nothing else — an empty state written over that silence would be the page
   * inventing an answer. It goes with the method it exists for.
   */
  readonly also?: readonly string[];
}

/** Where a strip is drawn: over the world, or inside a page. */
export type HotbarWhere = "hud" | "panel";

/** One strip on screen, and the ten elements it redraws. */
interface HotbarView {
  readonly where: HotbarWhere;
  readonly element: HTMLElement;
  readonly slots: readonly HTMLElement[];
  readonly labels: readonly (HTMLElement | null)[];
}

/**
 * The session's arrangement of carried things over the ten digit keys.
 *
 * One object, two views: {@link Hotbar.strip} builds a strip and remembers it,
 * so the HUD's corner and the inventory page's foot are the same ten slots seen
 * twice rather than two arrangements that have to be kept in step.
 *
 * **What it never does is decide anything about the world.** It holds display
 * numbers, it forgets one the moment the simulation stops saying it is carried,
 * and it lights whichever slot the simulation says is in hand. Every other
 * question — is this a weapon, would this swing, may this body hold it — belongs
 * to the simulation and is not asked here (`player.md` §24.3: *"the door asks
 * nothing about the thing"*).
 */
export class Hotbar {
  private readonly held: (number | null)[] = Array.from({ length: HOTBAR_SLOTS }, () => null);
  private readonly views: HotbarView[] = [];

  /** The words for what is in the slots, from the last read. Cleared with the slot. */
  private readonly words = new Map<number, string>();

  /** Which handle the simulation says is in hand, so an unchanged frame writes nothing. */
  private wielded: number | null = null;

  /** Presses waiting for `main.ts` to turn them into commands. */
  private queued: number[] = [];

  /** Whether anything is in a slot right now. The HUD strip's own question. */
  get arranged(): boolean {
    return this.held.some((item) => item !== null);
  }

  /** Whether every slot is taken, so the page can disable a door rather than refuse one. */
  get full(): boolean {
    return this.held.every((item) => item !== null);
  }

  /**
   * Builds a strip and remembers it.
   *
   * `"hud"` is the corner over the world and **takes no pointer events**: the
   * pointer is locked while a session is played, so a click there could not
   * reach a slot anyway, and a strip that turned events back on would be a
   * ten-box hole in `.hud`'s one rule (invariant 2, `ui/base.css`). It is a
   * readout of the keys. `"panel"` is the page's foot, where the pointer is
   * free and the slots are buttons a thumb can reach — which is the whole of how
   * a touch session uses the hotbar at all (`docs/decisions/input.md` §7).
   */
  strip(where: HotbarWhere): HTMLElement {
    const element = document.createElement("div");
    // The kit's two contexts, and the `hud__` name beside each (plan D2). The
    // kit sizes the slot and states the plate; what this client adds is where
    // the strip sits and what a slot says.
    element.className =
      where === "hud" ? "hud__hotbar hud-hotbar" : "hud__hotbar-strip hotbar-strip";
    // A group and not a bare label: a `div` with an `aria-label` and no role is
    // a label nothing is attached to.
    element.setAttribute("role", "group");
    element.setAttribute("aria-label", "hotbar");

    const slots: HTMLElement[] = [];
    const labels: (HTMLElement | null)[] = [];

    for (let index = 0; index < HOTBAR_SLOTS; index += 1) {
      const slot = document.createElement(where === "panel" ? "button" : "div");

      slot.className = "hud__hot-slot hot-slot";
      slot.dataset.slot = String(index);

      if (slot instanceof HTMLButtonElement) {
        slot.type = "button";
        slot.addEventListener("click", (event) => {
          // The panel button's two rules (see `button` above): a click in a page
          // must not reach the canvas, which is what asks for the pointer lock
          // back.
          event.stopPropagation();
          this.press(index);
        });
      }

      // Only the page has room for a word. Over the world the strip is chrome
      // the player is not looking at (`ui/kit.css`'s note on the two contexts),
      // and a caption there would be the interface talking over the world in the
      // one register the client keeps for the world itself (chunk U-1).
      const label = where === "panel" ? document.createElement("span") : null;

      if (label !== null) {
        label.className = "hud__hot-label";
        slot.append(label);
      }

      const digit = document.createElement("b");
      // The key, hung in the corner of the slot: `1`…`9` and then `0`, which is
      // the row a keyboard has and the order `input/controls.ts` binds.
      digit.textContent = String((index + 1) % 10);
      slot.append(digit);

      slots.push(slot);
      labels.push(label);
      element.append(slot);
    }

    const view: HotbarView = { where, element, slots, labels };

    this.views.push(view);
    this.draw(view);

    return element;
  }

  /**
   * Reconciles the arrangement against what the simulation says is carried, and
   * redraws.
   *
   * **A slot whose thing is no longer carried empties itself**, and that is the
   * only rule this object has about the world: a handle names a thing while the
   * thing is (`world/types.ts`'s `CarriedItem`), so a slot holding one that has
   * been dropped, eaten, worn or taken is a slot holding a number that may name
   * a stranger's boulder tomorrow. Emptying it here rather than refusing later
   * is the same answer `Command::Wield` gives — nothing, in silence.
   */
  read(hands: Hands): void {
    const carried = new Map<number, string>();

    for (const thing of hands.carried) {
      if (thing.item !== null) {
        carried.set(thing.item, thing.label);
      }
    }

    this.words.clear();

    for (const [index, item] of this.held.entries()) {
      if (item === null) {
        continue;
      }

      const label = carried.get(item);

      if (label === undefined) {
        this.held[index] = null;

        continue;
      }

      this.words.set(item, label);
    }

    this.wielded = hands.wielded;
    this.drawAll();
  }

  /**
   * Says which handle the simulation has in hand, and writes only when it moved.
   *
   * Asked at frame rate from `main.ts` while anything is arranged, because a
   * wield queued on one frame lands on a later tick and there is nothing else to
   * ask (invariant 8 is why the write is guarded rather than the read).
   */
  lit(wielded: number | null): void {
    if (this.wielded === wielded) {
      return;
    }

    this.wielded = wielded;
    this.drawAll();
  }

  /** Which slot a thing is in, or `null` for one that is in none. */
  slotOf(item: number): number | null {
    const index = this.held.indexOf(item);

    return index < 0 ? null : index;
  }

  /**
   * Puts a thing in the lowest free slot and answers which, or `null` when the
   * strip is full.
   *
   * Lowest-free rather than a chooser, and the page is why: a `1…0` picker on
   * every row of a list that can be eight rows long is eighty buttons, and what
   * a player wants from a hotbar is *this, somewhere I can reach*. Which slot it
   * landed in is on the row afterwards, and pressing the row again takes it out
   * — one control, two states, both reversible.
   */
  place(item: number): number | null {
    const already = this.slotOf(item);

    if (already !== null) {
      return already;
    }

    const free = this.held.indexOf(null);

    if (free < 0) {
      return null;
    }

    this.held[free] = item;
    this.drawAll();

    return free;
  }

  /** Takes a thing out of whatever slot it is in. */
  clear(item: number): void {
    const index = this.slotOf(item);

    if (index === null) {
      return;
    }

    this.held[index] = null;
    this.words.delete(item);
    this.drawAll();
  }

  /**
   * A slot was pressed — by its digit key or by a finger on the page's own
   * strip.
   *
   * An empty slot is a press that means nothing and queues nothing. It is not a
   * refusal said out loud, unlike every verb out in the world: there is no world
   * behind it to refuse and nothing was aimed at, and the slot itself is drawn
   * empty, which is the answer.
   */
  press(slot: number): void {
    const item = this.held[slot] ?? null;

    if (item === null) {
      return;
    }

    this.queued.push(item);
  }

  /**
   * Every handle pressed since the last call, consuming them.
   *
   * `main.ts` turns each into one `WorldSource.wield`. Drained the way every
   * latch in `input/controls.ts` is drained, and for its reason: a press that
   * arrived between two frames must not be lost, and one nobody consumed must
   * not fire the next time somebody does.
   */
  takeWields(): readonly number[] {
    const queued = this.queued;
    this.queued = [];

    return queued;
  }

  private drawAll(): void {
    for (const view of this.views) {
      this.draw(view);
    }
  }

  private draw(view: HotbarView): void {
    for (const [index, slot] of view.slots.entries()) {
      const item = this.held[index] ?? null;
      const label = item === null ? "" : (this.words.get(item) ?? "");

      // The kit's own two states, written as the kit spells them.
      slot.classList.toggle("empty", item === null);
      slot.classList.toggle("equipped", item !== null && item === this.wielded);

      const named = view.labels[index] ?? null;

      if (named !== null) {
        named.textContent = label;
      }

      // The word for the thing on every slot, including the corner strip's,
      // which has no room to draw one. A title is not a caption — it is the
      // label a pointer or a screen reader can reach, and the simulation's own
      // words either way.
      if (label === "") {
        slot.removeAttribute("title");
        slot.removeAttribute("aria-label");
      } else {
        slot.title = label;
        slot.setAttribute("aria-label", label);
      }
    }

    if (view.where === "hud") {
      // **Nothing arranged is nothing drawn.** Ten empty boxes in the corner of
      // every session nobody has ever pressed a digit in is exactly the chrome
      // the phase's minimal-chrome policy is about (`witness/arms.ts`'s note on
      // the loose prompt: a permanent line for the ninety-nine per cent of a
      // session spent looking at hillsides is chrome, and chrome is what a
      // player stops reading). The page's own strip is always drawn, because a
      // player who opened the page is asking about it.
      view.element.hidden = !this.arranged;
    }
  }
}
